import React, { useState, useEffect } from 'react';
import { FaSun, FaMoon } from 'react-icons/fa';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

const Header = ({ toggleTheme, isDarkMode }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const location = useLocation();
  
  // Check if we're on the project details page
  const isProjectDetailsPage = location.pathname.includes('/project/');
  const isCertificatesPage = location.pathname.includes('/certificates');
  const isWhy927Page = location.pathname.includes('/why-927');
  const isStandalonePage = isProjectDetailsPage || isCertificatesPage || isWhy927Page;

  useEffect(() => {
    // Simulate loading progress
    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + Math.random() * 15; // Random increment for realistic feel
      });
    }, 100);

    const timer = setTimeout(() => {
      setLoadingProgress(100);
      setTimeout(() => {
        setLoading(false);
      }, 500); // Small delay after reaching 100%
    }, 1500);

    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      clearTimeout(timer);
      clearInterval(progressInterval);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleScroll = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Render different navigation based on the current page
  const renderNavigation = () => {
    if (isStandalonePage) {
      return (
        <>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link 
              to="/" 
              className="flex items-center mr-6 px-4 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-white/10 backdrop-blur-sm border border-transparent hover:border-white/20 transition-all duration-300 ease-in-out"
              onClick={() => {
                if (isProjectDetailsPage) {
                  setTimeout(() => {
                    window.location.hash = "projects";
                  }, 100);
                }
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Home
            </Link>
          </motion.div>
          
          {/* Show Certificates link when not on certificates page */}
          {!isCertificatesPage && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Link 
                to="/certificates" 
                className="flex items-center mr-6 px-4 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-white/10 backdrop-blur-sm border border-transparent hover:border-white/20 transition-all duration-300 ease-in-out"
              >
                Certificates
              </Link>
            </motion.div>
          )}
          
          <motion.button 
            onClick={toggleTheme}
            className="p-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-gray-300 hover:text-white hover:bg-white/20 transition-all duration-300 ease-in-out shadow-lg hover:shadow-xl hover:scale-105 active:scale-95"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDarkMode ? <FaMoon className="text-yellow-400" /> : <FaSun className="text-yellow-500" />}
          </motion.button>
        </>
      );
    } else {
      return (
        <>
          {['about', 'skills', 'projects', 'services', 'contact'].map((item, index) => (
            <motion.a
              key={item}
              href={`#${item}`}
              onClick={(e) => handleScroll(e, item)}
              className="mr-2 px-4 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-white/10 transition-all duration-300 ease-in-out hover:scale-105 relative group"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              {item.charAt(0).toUpperCase() + item.slice(1)}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-400 to-purple-400 group-hover:w-full transition-all duration-300"></span>
            </motion.a>
          ))}
          
          {/* Certificates Link */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.5 }}
          >
            <Link 
              to="/certificates" 
              className="mr-2 px-4 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-white/10 transition-all duration-300 ease-in-out hover:scale-105 relative group"
            >
              Certificates
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-400 to-purple-400 group-hover:w-full transition-all duration-300"></span>
            </Link>
          </motion.div>
          
          <motion.button 
            onClick={toggleTheme}
            className="p-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-gray-300 hover:text-white hover:bg-white/20 transition-all duration-300 ease-in-out shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 ml-4"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDarkMode ? <FaMoon className="text-yellow-400" /> : <FaSun className="text-yellow-500" />}
          </motion.button>
        </>
      );
    }
  };

  // Render different mobile menu based on the current page
  const renderMobileMenu = () => {
    if (isStandalonePage) {
      return (
        <motion.div 
          className="fixed right-4 top-32 bg-gray-900/95 backdrop-blur-xl border border-white/20 text-white p-6 rounded-2xl shadow-2xl z-[100] min-w-[220px] max-w-[90vw]"
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <Link 
            to="/" 
            className="flex items-center mb-4 px-4 py-3 text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-300 ease-in-out"
            onClick={() => {
              if (isProjectDetailsPage) {
                setTimeout(() => {
                  window.location.hash = "projects";
                }, 100);
              }
              setIsMenuOpen(false);
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>
          
          {/* Show Certificates link when not on certificates page */}
          {!isCertificatesPage && (
            <Link 
              to="/certificates" 
              className="flex items-center mb-4 px-4 py-3 text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-300 ease-in-out"
              onClick={() => setIsMenuOpen(false)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Certificates
            </Link>
          )}
          
          <div className="border-t border-white/20 pt-4 mt-4">
            <button 
              onClick={() => {
                toggleTheme();
                setIsMenuOpen(false);
              }}
              className="w-full p-3 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 text-gray-300 hover:text-white hover:bg-white/20 transition-all duration-300 ease-in-out flex items-center justify-center"
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? <FaMoon className="text-yellow-400 mr-2" /> : <FaSun className="text-yellow-500 mr-2" />}
              {isDarkMode ? 'Dark Mode' : 'Light Mode'}
            </button>
          </div>
        </motion.div>
      );
    } else {
      return (
        <motion.div 
          className="fixed right-4 top-32 bg-gray-900/95 backdrop-blur-xl border border-white/20 text-white p-6 rounded-2xl shadow-2xl z-[100] min-w-[220px] max-w-[90vw]"
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          {['about', 'skills', 'projects', 'services', 'contact'].map((item, index) => (
            <motion.a 
              key={item}
              href={`#${item}`} 
              onClick={(e) => handleScroll(e, item)} 
              className="block mb-3 px-4 py-3 text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-300 ease-in-out"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2, delay: index * 0.05 }}
            >
              {item.charAt(0).toUpperCase() + item.slice(1)}
            </motion.a>
          ))}
          
          {/* Certificates Link for mobile */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2, delay: 0.25 }}
          >
            <Link 
              to="/certificates" 
              className="block mb-3 px-4 py-3 text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-300 ease-in-out"
              onClick={() => setIsMenuOpen(false)}
            >
              Certificates
            </Link>
          </motion.div>
          
          <div className="border-t border-white/20 pt-4 mt-4">
            <button 
              onClick={() => {
                toggleTheme();
                setIsMenuOpen(false);
              }}
              className="w-full p-3 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 text-gray-300 hover:text-white hover:bg-white/20 transition-all duration-300 ease-in-out flex items-center justify-center"
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? <FaMoon className="text-yellow-400 mr-2" /> : <FaSun className="text-yellow-500 mr-2" />}
              {isDarkMode ? 'Dark Mode' : 'Light Mode'}
            </button>
          </div>
        </motion.div>
      );
    }
  };

  return (
    <motion.header 
      className={`w-full text-white py-5 px-8 flex justify-between items-center shadow-lg z-[100] top-0 sticky transition-all duration-300 ease-in-out
        ${scrolled 
          ? 'bg-black/30 backdrop-blur-lg border-b border-white/10' 
          : 'bg-black/20 backdrop-blur-md border-b border-white/5'
        }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 100, damping: 20 }}
    >
      {/* Loading Progress Bar - Blueish Purple */}
      {loading && (
        <motion.div
          className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500/25 via-indigo-600/25 to-purple-600/25 backdrop-blur-sm"
          style={{ width: `${Math.min(loadingProgress, 100)}%` }}
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(loadingProgress, 100)}%` }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        />
      )}
      
      {/* Animated shimmer effect during loading - Blueish Purple */}
      {loading && (
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-blue-400/10 to-transparent animate-pulse" />
      )}

      <motion.div
        className="flex items-center relative z-10"
        whileHover={{ scale: 1.02 }}
        transition={{ type: "spring", stiffness: 300 }}
      >
        <Link to="/">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent cursor-pointer">
            {loading ? <Skeleton width={180} /> : 'rdarshan927'}
          </h1>
        </Link>
        {!loading && (
          <a 
            href="https://scistarter.org" 
            target="_blank" 
            rel="noopener noreferrer"
            className="ml-3 hover:scale-110 transition-transform duration-300"
            title="Foundations of Citizen Science"
          >
            <img 
              src="/images/verifiable Foundations of Citizen Science badge.png" 
              alt="Foundations of Citizen Science" 
              className="h-10 w-10 rounded-full shadow-lg hover:shadow-xl"
            />
          </a>
        )}
        <span className="ml-3 text-xs px-2 py-1 bg-amber-400/20 text-amber-400 rounded-full border border-amber-400/30">
          Not available for hire
        </span>
      </motion.div>

      {loading ? (
        <div className="hidden md:flex items-center space-x-4 relative z-10">
          <Skeleton width={80} height={32} className="rounded-lg" />
          <Skeleton width={80} height={32} className="rounded-lg" />
          <Skeleton width={80} height={32} className="rounded-lg" />
          <Skeleton width={80} height={32} className="rounded-lg" />
          <Skeleton width={100} height={32} className="rounded-lg" />
          <Skeleton width={40} height={40} className="rounded-full" />
        </div>
      ) : (
        <nav className="hidden md:flex items-center relative z-10">
          {renderNavigation()}
        </nav>
      )}

      {loading ? (
        <div className="md:hidden relative z-10">
          <Skeleton width={60} height={36} className="rounded-lg" />
        </div>
      ) : (
        <div className="md:hidden relative z-10">
          <motion.button 
            onClick={toggleMenu} 
            className="p-3 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 text-gray-300 hover:text-white hover:bg-white/20 transition-all duration-300 ease-in-out shadow-lg hover:shadow-xl"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Toggle menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </motion.button>
        </div>
      )}

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[90] md:hidden"
            onClick={() => setIsMenuOpen(false)}
          />
          {renderMobileMenu()}
        </>
      )}

      {/* Decorative gradient line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent" />
    </motion.header>
  );
};

export default Header;
